import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HealthResponseSchema } from '@wharf/shared';
import {
  Activity,
  Ban,
  BellOff,
  Building2,
  Database,
  FileText,
  Loader2,
  Mail,
  PenLine,
  Sparkles,
  Wrench,
  Zap,
} from 'lucide-react';
import { useMe, AuthError, goToLogin } from '@/lib/auth';
import { ConnectedAccounts } from '@/components/ConnectedAccounts';
import { SignaturesSettings } from '@/components/SignaturesSettings';
import { RulesSettings } from '@/components/RulesSettings';
import { AiSettings } from '@/components/AiSettings';
import { CrmSettings } from '@/components/CrmSettings';
import { TemplatesSettings } from '@/components/TemplatesSettings';
import { AiFiltersSettings } from '@/components/AiFiltersSettings';
import { BlockedDomainsSettings } from '@/components/BlockedDomainsSettings';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/settings')({
  component: Settings,
});

type SectionId =
  | 'accounts'
  | 'signatures'
  | 'templates'
  | 'aifilters'
  | 'blocked'
  | 'rules'
  | 'ai'
  | 'crm'
  | 'advanced';

const SECTIONS: { id: SectionId; label: string; icon: typeof Mail; desc: string }[] = [
  { id: 'accounts', label: 'Accounts', icon: Mail, desc: 'Connected inboxes & access' },
  { id: 'signatures', label: 'Signatures', icon: PenLine, desc: 'Per-inbox email signatures' },
  { id: 'templates', label: 'Templates', icon: FileText, desc: 'Canned responses for composers' },
  { id: 'aifilters', label: 'AI filters', icon: BellOff, desc: 'Mute/notify filters from emails' },
  { id: 'blocked', label: 'Blocked', icon: Ban, desc: 'Senders sent straight to Trash' },
  { id: 'rules', label: 'Rules', icon: Zap, desc: 'Automate incoming mail' },
  { id: 'ai', label: 'AI', icon: Sparkles, desc: 'Provider & model' },
  { id: 'crm', label: 'Clients', icon: Building2, desc: 'Context for AI summaries' },
  { id: 'advanced', label: 'Advanced', icon: Wrench, desc: 'System status' },
];

function Settings() {
  const { data: me, isLoading, error } = useMe();
  const [section, setSection] = useState<SectionId>('accounts');

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />
      </div>
    );
  }
  if (error instanceof AuthError) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-sm rounded-2xl border bg-card p-8 text-center shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight">Sign in to continue</h1>
          <Button onClick={goToLogin} className="mt-6 w-full">
            Sign in with Clougency
          </Button>
        </div>
      </div>
    );
  }
  if (!me) return null;

  const active = SECTIONS.find((s) => s.id === section)!;

  return (
    <div className="h-full overflow-y-auto">
    <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {me.user.name || me.user.email} · {me.user.email}
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-[200px_1fr]">
        {/* Section nav */}
        <nav className="flex gap-1 overflow-x-auto md:flex-col md:overflow-visible">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const on = s.id === section;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSection(s.id)}
                className={cn(
                  'flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  on
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )}
              >
                <Icon className="size-4 shrink-0" strokeWidth={1.75} />
                <span>{s.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Active section */}
        <div className="min-w-0 space-y-4">
          <div className="hidden md:block">
            <h2 className="text-lg font-semibold tracking-tight">{active.label}</h2>
            <p className="text-sm text-muted-foreground">{active.desc}</p>
          </div>
          {section === 'accounts' && <ConnectedAccounts />}
          {section === 'signatures' && <SignaturesSettings />}
          {section === 'templates' && <TemplatesSettings />}
          {section === 'aifilters' && <AiFiltersSettings />}
          {section === 'blocked' && <BlockedDomainsSettings />}
          {section === 'rules' && <RulesSettings />}
          {section === 'ai' && <AiSettings />}
          {section === 'crm' && <CrmSettings />}
          {section === 'advanced' && <SystemHealth />}
        </div>
      </div>
    </div>
    </div>
  );
}

function SystemHealth() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await fetch('/api/health');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return HealthResponseSchema.parse(await res.json());
    },
    refetchInterval: 10_000,
  });

  return (
    <section className="rounded-lg border bg-card p-6">
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        System status
      </h3>
      {isLoading && <p className="text-sm text-muted-foreground">Checking…</p>}
      {error && (
        <p className="text-sm text-destructive">
          Could not reach API: {error instanceof Error ? error.message : 'unknown'}
        </p>
      )}
      {data && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat icon={Activity} label="API" status={data.status} version={data.version} />
          <Stat icon={Database} label="Postgres" status={data.checks.postgres} />
          <Stat icon={Zap} label="Valkey" status={data.checks.valkey} />
        </div>
      )}
    </section>
  );
}

function Stat({
  icon: Icon,
  label,
  status,
  version,
}: {
  icon: typeof Activity;
  label: string;
  status: string;
  version?: string;
}) {
  const ok = status === 'ok';
  return (
    <div className="flex items-start gap-3 rounded-md border bg-background p-4">
      <Icon
        className={cn('mt-0.5 size-5 shrink-0', ok ? 'text-emerald-500' : 'text-destructive')}
        strokeWidth={1.75}
      />
      <div className="min-w-0">
        <div className="text-sm font-medium">{label}</div>
        <div className={cn('text-xs', ok ? 'text-muted-foreground' : 'text-destructive')}>
          {status}
          {version && ` · v${version}`}
        </div>
      </div>
    </div>
  );
}
